import { useNavigate } from "react-router-dom";
import { Compass, ArrowRight } from "lucide-react";

/**
 * HelpNowButton
 * A prominent, always-visible floating call-to-action for anyone who feels
 * overwhelmed and wants to jump straight to Asher without reading the whole
 * landing page. Coral (secondary) with a breathing halo animation.
 *
 * Pinned bottom-right. On mobile it collapses to a compact pill.
 */
export default function HelpNowButton() {
  const navigate = useNavigate();

  return (
    <div
      data-testid="help-now-fab"
      className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-40"
    >
      <style>{`
        @keyframes help-pulse {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(224, 123, 84, 0.55),
                        0 12px 30px -4px rgba(224, 123, 84, 0.55);
          }
          50% {
            box-shadow: 0 0 0 14px rgba(224, 123, 84, 0),
                        0 16px 34px -4px rgba(224, 123, 84, 0.7);
          }
        }
        @keyframes help-glow {
          0%, 100% { opacity: 0.35; transform: scale(1); }
          50%      { opacity: 0.75; transform: scale(1.15); }
        }
        .help-pulse-btn { animation: help-pulse 2.6s ease-in-out infinite; }
        .help-glow-halo { animation: help-glow 2.6s ease-in-out infinite; }
      `}</style>

      <div className="relative">
        {/* Soft outer glow halo — breathes to draw the eye */}
        <div
          className="absolute -inset-3 rounded-full bg-secondary/40 blur-xl help-glow-halo pointer-events-none"
          aria-hidden="true"
        />

        <button
          data-testid="help-now-button"
          onClick={() => navigate("/chat?from=help-now")}
          className="help-pulse-btn relative flex items-center gap-2.5 md:gap-3 px-4 md:px-5 py-3 md:py-3.5 bg-secondary text-white rounded-full font-body font-bold text-sm md:text-base shadow-xl hover:shadow-2xl hover:-translate-y-0.5 hover:bg-[#C96540] active:scale-95 transition-all"
          aria-label="Need help now? Start a conversation with Asher"
        >
          <span className="relative flex-shrink-0 w-8 h-8 md:w-9 md:h-9 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
            <Compass className="w-4 h-4 md:w-5 md:h-5" strokeWidth={2.2} />
          </span>
          <span className="flex flex-col items-start leading-tight">
            <span className="text-[10px] md:text-xs font-body font-bold uppercase tracking-[0.15em] opacity-90">
              Need help now?
            </span>
            <span className="flex items-center gap-1 text-sm md:text-base font-heading font-extrabold">
              Talk to Asher
              <ArrowRight className="w-3.5 h-3.5 md:w-4 md:h-4 ml-0.5" />
            </span>
          </span>
        </button>
      </div>
    </div>
  );
}
